/**
 * Orders page header — the prototype's `.pagehd`.
 *
 * "Orders" as a display h1,
 * and on the right the order count, the last-fetched stamp and the Deeliver
 * app order sound controls (mute, sound picker).
 */

import { Box, Typography, Stack } from '@mui/material';
import { useAdminTheme } from '../../../admin-theme';
import { FONT_DATA, FONT_DISPLAY } from '../../../theme/grounds';
import { monoLabelSx } from '../../reskin/primitives';
import OrderSoundControls from '../../OrderSoundControls';
import SoundVisualizer from './SoundVisualizer';

interface HUDHeaderProps {
  isSoundPlaying: boolean;
  /** Orders matching the current filters (react-admin's `total`). */
  total?: number;
  /** When the list was last fetched. */
  updatedAt?: Date | null;
}

export default function HUDHeader({
  isSoundPlaying,
  total,
  updatedAt,
}: HUDHeaderProps) {
  const { colors, glassHeaderStyle } = useAdminTheme();

  return (
    <Box sx={{ ...glassHeaderStyle, px: 2.75, py: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography
            component="h1"
            sx={{ fontFamily: FONT_DISPLAY, fontStretch: '118%', fontWeight: 800, fontSize: '1.5rem', letterSpacing: '-0.02em', lineHeight: 1, color: colors.textPrimary, m: 0 }}
          >
            Orders
          </Typography>
          <SoundVisualizer isPlaying={isSoundPlaying} />
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1.5}>
          {typeof total === 'number' && (
            <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.75rem', color: colors.textMuted, display: { xs: 'none', sm: 'block' }, fontVariantNumeric: 'tabular-nums' }}>
              {total.toLocaleString('en-ZA')} {total === 1 ? 'order' : 'orders'}
              {updatedAt && (
                <Box component="span" sx={{ ml: 1.5 }}>Updated {updatedAt.toLocaleTimeString()}</Box>
              )}
            </Typography>
          )}

          <OrderSoundControls channel="deeliver" />
        </Stack>
      </Stack>
    </Box>
  );
}
